<template>
  <div v-if="!isLoadingInit" class="meeting-presence-block">
    <b-alert
      v-if="$route.name == 'meeting-presence'"
      :show="!meetingHasResolutions || !meetingHasParticipants"
      variant="warning"
    >
      <div v-if="!meetingHasResolutions">
        Aucune résolution ..
      </div>
      <div v-if="!meetingHasParticipants">
        Aucun propriétaire present ..
      </div>
    </b-alert>

    <appVotingLimitRulesInfo
      :voting-rules-pass="votingRulesPass"
      :total-percent-presence="totalPercentPresence"
      :total-tantiemes-charge-general="totalTantiemesChargeGeneral"
      :total-present-or-represented-tantiemes-charge-general="
        totalPresentOrRepresentedTantiemesChargeGeneral
      "
    />

    <b-row v-if="$route.name !== 'meeting-start'" class="my-2">
      <b-col>
        <h2>{{ meeting.name }}</h2>
        <hr />
      </b-col>
    </b-row>

    <!-- Add participant -->
    <b-row
      v-if="meeting.allowed_modalities.physical || meeting.allowed_modalities.video"
      class="mb-4 sticky-multiselect-add-participants align-self-start"
    >
      <b-col>
        <label><h4>Ajouter un participant</h4></label>
        <v-multiselect
          ref="multiselect-participants"
          :show-labels="false"
          :options="addParticipantsOptions"
          group-values="items"
          group-label="group"
          :group-select="false"
          placeholder="Tapez pour rechercher"
          track-by="identifier"
          label="search"
          open-direction="bottom"
          :close-on-select="false"
          @open="showMainMultiselect = true"
          @close="showMainMultiselect = false"
        >
          <template slot="option" slot-scope="props">
            <div v-if="props.option.$groupLabel" class="select-option-group-label">
              <strong class="text-primary">{{ props.option.$groupLabel }}</strong>
            </div>
            <div v-else class="select-option-item">
              <div v-if="props.option.type == 'owner'">
                <div class="select-option-main d-flex align-items-center">
                  <span class="mr-2">
                    {{ props.option.name }}
                  </span>
                  <div>
                    <appBOTantiemes :building-owner-data="props.option" />
                  </div>
                </div>
                <div class="select-option-description">
                  {{
                    props.option.building_lots
                      ? props.option.building_lots.map(x => x.name).join(", ")
                      : ""
                  }}
                </div>
              </div>
              <div v-else>
                <div class="select-option-main">
                  {{ props.option.name }}
                </div>
              </div>
              <div class="select-option-buttons">
                <b-button
                  v-if="meeting.allowed_modalities.video"
                  class="mx-2 border border-dark"
                  variant="info"
                  @click="onSelectOwnerOrContact(props.option, 'video')"
                >
                  Vidéo
                </b-button>
                <b-button
                  v-if="meeting.allowed_modalities.physical"
                  class="mx-2 border border-dark"
                  variant="light"
                  @click="onSelectOwnerOrContact(props.option, 'physical')"
                >
                  Physique
                </b-button>
              </div>
            </div>
          </template>
          <template slot="beforeList">
            <div class="select-beforeList-buttons">
              <b-button
                v-if="meeting.allowed_modalities.video"
                class="mx-2 border border-dark"
                variant="info"
                @click="openModalCreateContact('video')"
              >
                Vidéo
              </b-button>
              <b-button
                v-if="meeting.allowed_modalities.physical"
                class="mx-2 border border-dark"
                variant="light"
                @click="openModalCreateContact('physical')"
              >
                Physique
              </b-button>
            </div>
            <div class="select-beforeList-main">
              <div><font-awesome-icon icon="plus-circle" /> Ajouter une nouvelle contact.</div>
            </div>
          </template>
          <span slot="noResult" class="mx-2">
            Oops! Aucun élément trouvé. Pensez à modifier la requête de recherche.
          </span>
        </v-multiselect>
      </b-col>
    </b-row>

    <b-row class="justify-content-center">
      <b-col cols="12" :md="$route.name == 'meeting-start' ? '12' : '8'">
        <appPhysicalPresenceTable
          v-if="meeting.allowed_modalities.physical"
          class="mandates-table mb-8"
          :presence-mandates="allMandates"
        />

        <appVideoPresence
          v-if="meeting.allowed_modalities.video"
          :meeting="meeting"
          :presence-mandates="allMandates"
        />

        <appDummyVideoPresenceTable
          v-if="meeting.allowed_modalities.video"
          class="mandates-table mb-8"
          :presence-mandates="allMandates"
        />

        <appCorrespondencePresenceTable
          v-if="mandatesByCorrespondence && mandatesByCorrespondence.length > 0"
          class="mandates-table correspondence my-8"
          :mandates-by-correspondence="mandatesByCorrespondence"
        />
      </b-col>

      <!-- Indicators -->
      <b-col
        v-if="$route.name !== 'meeting-start'"
        class="order-sm-first order-md-last sticky-presence-indicators align-self-start"
        cols="10"
        md="4"
      >
        <b-row>
          <b-col>
            <b-card>
              <b-row>
                <b-col>
                  <div class="mb-2">
                    <div class="mb-1">
                      {{ $t("meetings.presence_count") }}
                      <strong>{{ percentPresence }} %</strong>
                    </div>
                    <b-progress :max="100">
                      <b-progress-bar :value="percentPresence" />
                    </b-progress>
                  </div>
                  <div
                    v-for="(votingCategoryPercentage, index) in percentagesPerVotingCategories"
                    :key="index"
                    class="mb-2"
                  >
                    <div class="mb-1">
                      {{ "Taux de Présence (" + votingCategoryPercentage.name + ")" }}
                      <strong>{{ votingCategoryPercentage.percent }} %</strong>
                    </div>

                    <b-progress :max="100">
                      <b-progress-bar :value="votingCategoryPercentage.percent" />
                    </b-progress>
                  </div>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
        <div>
          <appPresenceIndicatorsMenu
            :all-mandates="allMandates.length"
            :present-mandates="presentMandates.length"
            :represented-mandates="representedMandates.length"
            :correspondence-mandates="mandatesByCorrespondence.length"
            :absent-mandates="absentMandatesLength"
            :video-mandates="dummyVideoMandates.length"
            :represented-video-mandates="representedDummyVideoMandates.length"
            :is-before-meeting-start="true"
          />
        </div>

        <b-row v-if="$route.name == 'meeting-presence'" class="my-4">
          <b-col cols="auto" class="my-1">
            <b-button variant="light" @click="generarePresencePdf">
              <i class="fa fa-meetup" aria-hidden="true"></i>
              <font-awesome-icon icon="file-pdf" />
              <span class="mx-1">Feuille de présence</span>
            </b-button>
          </b-col>
          <b-col class="text-right">
            <!-- FIXME: needs work.. -->
            <!-- <b-button
              v-if="!meetingHasParticipants"
              variant="outline-danger"
              class="mr-5"
              @click="showNoParticipantsPopUp"
            >
              <span>Aucun propriétaire present</span>
            </b-button> -->

            <b-button
              :disabled="isLoadingStartMeeting || !votingRulesPass || !meetingHasResolutions"
              class="button btn-red"
              @click="START_MEETING"
            >
              <font-awesome-icon
                v-if="!isLoadingStartMeeting"
                class="agv-button-icon"
                icon="play"
              />
              <font-awesome-icon v-else class="agv-button-icon" icon="spinner" pulse />
              <span>Commencer</span>
            </b-button>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Modal Create Contact -->
    <b-modal id="modal-create-contact" size="lg" hide-footer centered>
      <modalCreateContact
        :building-id="meeting.building_id"
        :is-in-meeting-start="isInMeetingStart"
        @closeModal="closeModalCreateContact"
        @setContact="setContact"
      />
    </b-modal>
    <!-- Modal Ask Building Owner -->
    <b-modal id="modal-ask-building-owner" size="lg" hide-footer centered>
      <modalAskBuildingOwner
        :options="assignBuildingOwnerOptions"
        @setMandate="setMandate"
        @onOpenSelect="showAskBuildingOwnerMultiselect = true"
        @onCloseSelect="showAskBuildingOwnerMultiselect = false"
      />
    </b-modal>

    <b-modal id="modalSelectPvOrPresencePdf" size="lg" hide-footer centered>
      <modalSelectPvOrPresencePdf
        @generatePresencePdf="GENERATE_PRESENCE_PDF"
        @generatePvPdf="GENERATE_PV_PDF"
      />
    </b-modal>
  </div>
  <appLoader v-else />
</template>

<script>
import appCorrespondencePresenceTable from "@/components/meetings/meeting-presence/CorrespondencePresenceTable";
import modalCreateContact from "@/components/modals/create-contact/CreateContact";
import modalAskBuildingOwner from "@/components/modals/create-contact/AskBuildingOwner";
import modalSelectPvOrPresencePdf from "@/components/b-table-components/modals/SelectPvOrPresencePdf";
import appVotingLimitRulesInfo from "@/components/snippets/VotingLimitRulesInfo";
import appBOTantiemes from "@/components/snippets/BuildingOwnerTantiemes";

import MandateMW from "@/middlewares/Mandate";
import MeetingMW from "@/middlewares/Meeting";
import ContactMW from "@/middlewares/Contact";
import ConfigurationMW from "@/middlewares/Configuration";
import MeetingResolutionMW from "@/middlewares/MeetingResolution";
import MediaMW from "@/middlewares/Media";
import PDFMW from "@/middlewares/PDF";
import ResolutionVoteMW from "@/middlewares/ResolutionVote";

import MandateORM from "@/store/orm-axios/models/Mandate";
import MeetingORM from "@/store/orm-axios/models/Meeting";
import ContactORM from "@/store/orm-axios/models/Contact";
import ConfigurationORM from "@/store/orm-axios/models/Configuration";
import MediaORM from "@/store/orm-axios/models/Media";
import MeetingCorrespondenceVoteORM from "@/store/orm-axios/models/MeetingCorrespondenceVote";
import ResolutionVoteORM from "@/store/orm-axios/models/ResolutionVote";
import BuildingOwnerORM from "@/store/orm-axios/models/BuildingOwner";
import VotingCategoryORM from "@/store/orm-axios/models/VotingCategory";

import meetingDetailsMixin from "@/mixins/meeting-details";
import mandatePresenceMixin from "@/mixins/mandate-presence";
import minutesMixin from "@/mixins/minutes";
import meetingPresenceSelectOptionsMixins from "@/mixins/meeting-presence-select-options";
import meetingPresenceMixin from "@/mixins/meeting-presence";

import { mapActions } from "vuex";
import usePDFs from "@/use/usePDFs.js";
import useAlerts from "@/use/useAlerts.js";

export default {
  components: {
    modalCreateContact,
    modalAskBuildingOwner,
    appCorrespondencePresenceTable,
    appPhysicalPresenceTable: () =>
      import("@/components/meetings/meeting-presence/PhysicalPresenceTable"),
    appVideoPresence: () => import("@/components/meetings/meeting-presence/VideoPresence"),
    appDummyVideoPresenceTable: () =>
      import("@/components/meetings/meeting-presence/DummyVideoPresenceTable"),
    appPresenceIndicatorsMenu: () => import("@/components/snippets/PresenceIndicatorsMenu"),
    modalSelectPvOrPresencePdf,
    appVotingLimitRulesInfo,
    appBOTantiemes,
  },
  mixins: [
    meetingPresenceMixin,
    mandatePresenceMixin,
    meetingDetailsMixin,
    minutesMixin,
    meetingPresenceSelectOptionsMixins,
  ],
  props: {
    isInMeetingStart: {
      type: Boolean,
      default: false,
    },
    // use it to indicate voting limit rules errors
    errorsVotingLimitRules: {
      type: Array,
      default: () => {
        return [];
      },
    },
    totalPercentPresence: {
      type: Number,
      default: 0,
    },
    totalTantiemesChargeGeneral: {
      type: Number,
      default: 0,
    },
    totalPresentOrRepresentedTantiemesChargeGeneral: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedOwnerOrContact: null,
      selectedOwnerOrContactPresenceType: null,
      isLoadingInit: true,
      isLoadingStartMeeting: false,
      pdfFooterMessage: "",
      showMainMultiselect: false,
      addParticipantsOptions: [],
      showAskBuildingOwnerMultiselect: false,
      assignBuildingOwnerOptions: [],
      buildingOwners: [],
      partsForTotalMandates: [],
      percentagesPerVotingCategories: [],
    };
  },
  computed: {
    meeting() {
      return MeetingORM.query()
        .where("id", this.$route.params.id)
        .with("meeting_resolutions")
        .first();
    },
    allMandates() {
      return MandateORM.getters("getMandatesForMeetingPresence")(this.meeting.id);
    },
    mandatesByCorrespondence() {
      return this.$_.filter(this.allMandates, mandate => {
        return (
          (mandate.showed_up == 0 || mandate.showed_up == null) &&
          mandate.presence_status == this.mandatePresenceStatuses.correspondence
        );
      });
    },
    dummyVideoMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.building_owner_id == mandate.representant_owner_id &&
          mandate.presence_status == this.mandatePresenceStatuses.video &&
          !!mandate.dummy_video_presence_status
        );
      });
    },
    representedDummyVideoMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.building_owner_id != mandate.representant_owner_id &&
          mandate.presence_status == this.mandatePresenceStatuses.pouvoir_by_video &&
          !!mandate.dummy_video_presence_status
        );
      });
    },
    presentMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return mandate.presence_status == this.mandatePresenceStatuses.physical;
      });
    },
    representedMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return mandate.presence_status == this.mandatePresenceStatuses.pouvoir;
      });
    },
    absentMandatesLength() {
      return (
        this.allMandates.length -
        this.presentMandates.length -
        this.representedMandates.length -
        this.mandatesByCorrespondence.length -
        this.dummyVideoMandates.length -
        this.representedDummyVideoMandates.length
      );
    },
    percentPresence() {
      var total = this.allMandates.length;
      var totalPresent =
        this.presentMandates.length +
        this.representedMandates.length +
        this.dummyVideoMandates.length +
        this.representedDummyVideoMandates.length +
        this.mandatesByCorrespondence.length;

      var percentPresence = (totalPresent * 100) / total;
      if (percentPresence) {
        return this.formatNumber(percentPresence);
      } else {
        return 0;
      }
    },
    votingRulesPass() {
      if (this.errorsVotingLimitRules.length > 0) {
        return false;
      }
      return true;
    },
    meetingHasResolutions() {
      if (this.meeting.meeting_resolutions.length > 0) {
        return true;
      }
      return false;
    },
    meetingHasParticipants() {
      var self = this;
      var hasAtLeastOneParticipant = self.$_.find(self.allMandates, mandate => {
        return mandate.showed_up == 1 || !self.checkIfMandateIsAbsent(mandate);
      });
      if (hasAtLeastOneParticipant) {
        return true;
      }
      return false;
    },
    configuration() {
      // There will be only one configuration for each agency id
      return ConfigurationORM.query().where("agency_id", this.agencyId).first();
    },
    contacts() {
      return ContactORM.query().where("building_id", this.meeting.building_id).get();
    },
    mediaSignatures() {
      return MediaORM.getters("getAll");
    },
  },
  watch: {
    configuration() {
      this.getPdfConfigs();
    },
    showMainMultiselect(val) {
      if (val) {
        this.addParticipantsOptions = this.getBOsCsNotShowUpNotIntendToOrRepresentedGroupedOpts();
      } else {
        this.addParticipantsOptions = [];
      }
    },
    showAskBuildingOwnerMultiselect(val) {
      if (val) {
        this.assignBuildingOwnerOptions = this.getBOsNotShowedUpOrRepresentedOpts();
      } else {
        this.assignBuildingOwnerOptions = [];
      }
    },
    contacts(newValue, oldValue) {
      if (newValue.length > oldValue.length) {
        this.setContactsData(this.meeting.building_id);
      }
    },
    allMandates(newValue, oldValue) {
      if (newValue != oldValue) {
        // NOTE: Update percentagesPerVotingCategories only when a mandate suffers an update
        this.percentagesPerVotingCategories = this.getPercentagePerVotingCategory();
      }
    },
  },
  created() {
    this.isLoadingInit = true;

    ConfigurationMW.FETCH(this.agencyId).then(() => {
      this.getPdfConfigs();
    });

    this.isLoadingInit = false;
    this.$emit("loadingEnded");

    // NOTE: Get all buildingOwners for mandates and also partsForTotalMandates to improve UI performance
    this.getBuildingOwnersWithVotingCategoriesForMandates();
    this.partsForTotalMandates = this.getTotalPartsOfVotingCategoryFromMandates(this.allMandates);
    // NOTE: Init percentagesPerVotingCategories
    this.percentagesPerVotingCategories = this.getPercentagePerVotingCategory();
  },
  methods: {
    async START_MEETING() {
      try {
        // ???: || !this.meetingHasParticipants
        if (!this.votingRulesPass || !this.meetingHasResolutions) {
          return;
        }

        const questionResult = await this.$swal(this.alertQuestion());

        if (questionResult.value) {
          this.isLoadingStartMeeting = true;

          await this.updateResolutionsNumbers(this.meeting.id);

          await MeetingMW.START(this.meeting);

          // TODO: ???: start meeting timer

          this.isLoadingStartMeeting = false;
          this.$router.push({ name: "meeting-start", params: { id: this.meeting.id } });
        }
      } catch (err) {
        this.isLoadingStartMeeting = false;
        console.error(err);
        this.$swal(this.alertError());
      }
    },

    async updateResolutionsNumbers(meetingId) {
      await MeetingResolutionMW.UPDATE_RESOLUTIONS_NUMBERS(meetingId);
    },
    showNoParticipantsPopUp() {
      this.$bvModal.show("modalSelectPvOrPresencePdf");
    },

    GENERATE_PRESENCE_PDF() {
      this.$bvModal.hide("modalSelectPvOrPresencePdf");
      // FIXME: needs work..
    },
    GENERATE_PV_PDF() {
      this.$bvModal.hide("modalSelectPvOrPresencePdf");
      // FIXME: needs work..
    },

    onSelectOwnerOrContact(selectedOwnerOrContact, selectedOwnerOrContactPresenceType) {
      this.$refs["multiselect-participants"].isOpen = false;
      this.showMainMultiselect = false;
      this.selectedOwnerOrContact = selectedOwnerOrContact;
      this.selectedOwnerOrContactPresenceType = selectedOwnerOrContactPresenceType;

      if (selectedOwnerOrContact.type == "contact") {
        this.$bvModal.show("modal-ask-building-owner");
      } else if (selectedOwnerOrContact.type == "owner") {
        this.ADD_MANDATE(selectedOwnerOrContact.id);
      }
    },
    async ADD_MANDATE(buildingOwnerId, setContactArrivedEvent = true, updateMeetingMinutes = true) {
      try {
        this.$store.state.showLoaderOverlay = true;

        var defaultMandate = MandateORM.query()
          .where("meeting_id", this.meeting.id)
          .where("building_owner_id", buildingOwnerId)
          .first();

        const isFromCorrespondencePresence =
          defaultMandate.presence_status == this.mandatePresenceStatuses.correspondence
            ? true
            : false;

        // set mandate representant id
        defaultMandate.showed_up = 1;
        defaultMandate.representant_contact_id =
          this.selectedOwnerOrContact.type == "contact" ? this.selectedOwnerOrContact.id : null;
        defaultMandate.representant_owner_id =
          this.selectedOwnerOrContact.type == "owner" ? this.selectedOwnerOrContact.id : null;

        // if by video, set dummy_video_presence to true + ignore checking last seen date in meeting start if so..
        defaultMandate.dummy_video_presence_status =
          this.selectedOwnerOrContactPresenceType == "video" ? 1 : 0;

        // set mandate presence status
        if (defaultMandate.representant_owner_id == defaultMandate.building_owner_id) {
          defaultMandate.presence_status =
            this.selectedOwnerOrContactPresenceType == "video"
              ? this.mandatePresenceStatuses.video
              : this.mandatePresenceStatuses.physical;
        } else if (defaultMandate.representant_owner_id || defaultMandate.representant_contact_id) {
          defaultMandate.presence_status =
            this.selectedOwnerOrContactPresenceType == "video"
              ? this.mandatePresenceStatuses.pouvoir_by_video
              : this.mandatePresenceStatuses.pouvoir;
        } else if (
          defaultMandate.representant_owner_id == null &&
          defaultMandate.representant_contact_id == null
        ) {
          // ???: ??
          defaultMandate.presence_status = this.mandatePresenceStatuses.not_present;
        }

        // NOTE: for meeting minutes (participant-arrived event)
        MandateORM.update({
          where: defaultMandate.id,
          data: defaultMandate,
        });
        await MandateMW.PUT(defaultMandate, false).then(async () => {
          if (this.selectedOwnerOrContact.type == "contact") {
            if (setContactArrivedEvent)
              await this.setContactArrivedEvent(this.selectedOwnerOrContact.id);
            await this.setMandateAddedEvent(defaultMandate.id, false);

            // Also update contact with building_owner_id
            let contact = ContactORM.find(this.selectedOwnerOrContact.id);
            await ContactMW.PUT(contact);
          } else {
            await this.setParticipantArrivedEvent(defaultMandate.id, false);
          }

          if (updateMeetingMinutes) {
            await this.updateMeetingMinutes();
          }

          // If mandate is from correspondence, set all votes from meetingCorrespondenceVotes
          if (isFromCorrespondencePresence) {
            await ResolutionVoteMW.FETCH_BY_MANDATE(defaultMandate.id);
            const resolutionVotes = ResolutionVoteORM.getters("getResolutionVotesByMandate")(
              defaultMandate.id
            );
            let meetingCorrespondenceVote = MeetingCorrespondenceVoteORM.getters("getByMandateId")(
              defaultMandate.id
            );
            meetingCorrespondenceVote.proposed_votes.forEach(proposed_vote => {
              let resolutionVote = resolutionVotes.find(
                item => item.meeting_has_resolution_id == proposed_vote.meeting_resolution_id
              );
              if (resolutionVote) {
                resolutionVote.vote = proposed_vote.vote;
                ResolutionVoteMW.PUT(resolutionVote, false);
              }
            });
          }

          this.$store.state.showLoaderOverlay = false;
        });
      } catch (err) {
        this.$store.state.showLoaderOverlay = false;
        console.error(err);
        this.$swal(this.alertError());
      }
    },
    openModalCreateContact(selectedOwnerOrContactPresenceType) {
      this.$refs["multiselect-participants"].isOpen = false;
      this.showMainMultiselect = false;
      this.selectedOwnerOrContactPresenceType = selectedOwnerOrContactPresenceType;

      this.$bvModal.show("modal-create-contact");
    },
    closeModalCreateContact() {
      this.$bvModal.hide("modal-create-contact");
    },
    setContact(contact) {
      // HACK: normally this would be a more complex object, but here we set only the necesarry info for adding a mandate
      this.selectedOwnerOrContact = {
        type: "contact",
        id: contact.id,
      };
      this.$bvModal.hide("modal-create-contact");
      this.$bvModal.show("modal-ask-building-owner");
    },
    async setMandate(buildingOwnerId) {
      this.$bvModal.hide("modal-ask-building-owner");

      await this.ADD_MANDATE(buildingOwnerId, true, false);

      var hasOtherPouvoirsAssigned = this.checkIfContactHasOtherPouvoirsAssigned(
        this.selectedOwnerOrContact.id,
        buildingOwnerId
      );
      for (let mandate of hasOtherPouvoirsAssigned) {
        await this.ADD_MANDATE(mandate.building_owner_id, false, false);
      }

      this.updateMeetingMinutes();
    },
    checkIfContactHasOtherPouvoirsAssigned(contactId, buildingOwnerId) {
      return MandateORM.query()
        .where(mandate => {
          return (
            mandate.meeting_id == this.meeting.id &&
            mandate.representant_contact_id == contactId &&
            mandate.building_owner_id != buildingOwnerId &&
            mandate.presence_status == null
          );
        })
        .get();
    },
    async updateMeetingMinutes() {
      var m = MeetingORM.find(this.meeting.id);
      await MeetingMW.PUT(m, false).catch(err => {
        console.error(err);
      });
    },
    getPdfConfigs() {
      // Get current pdf_configs value
      if (this.configuration && this.configuration.settings.length > 0) {
        this.configuration.settings.forEach(element => {
          if (element.pdf_footer) {
            this.pdfFooterMessage = element.pdf_footer;
          }
        });
      }
    },
    async generarePresencePdf() {
      // check if the Agency has a logo
      if (localStorage.getItem("userMediaId")) {
        this.$store.state.showLoaderOverlay = true;
        let meeting = MeetingORM.query()
          .with("meeting_resolutions.children_resolutions")
          .where("id", this.$route.params.id)
          .first();

        await MediaMW.MEDIA_GET_SIGNATURES(meeting.id);

        const idLogoAgency = localStorage.getItem("userMediaId");
        MediaMW.GET_LOGO_IN_BASE64(idLogoAgency)
          .then(async logo => {
            meeting["logoInBase64"] = logo["file_as_base64_string"];

            const { generateTemplatePresence, downloadDialog } = usePDFs();
            generateTemplatePresence(meeting, this.pdfFooterMessage, this.mediaSignatures).then(
              template => {
                PDFMW.MEETING_PDF_CREATE_SAVE_RETURN(this.$route.params.id, {
                  presence: template,
                }).then(presencePDF => {
                  downloadDialog(
                    presencePDF.response.data.document,
                    "Présence " + meeting.name,
                    "pdf"
                  );
                  this.$store.state.showLoaderOverlay = false;
                });
              }
            );
          })
          .catch(err => {
            this.$store.state.showLoaderOverlay = false;
            const { alertErrorAndReport } = useAlerts();
            alertErrorAndReport(
              err,
              "Impossible d'extraire le logo !",
              "Veuillez vérifier votre connexion Internet."
            );
          });
      } else {
        this.$swal({
          icon: "warning",
          title: "Vous n'avez spécifié aucun logo pour votre agence !",
          html: "Accédez au profil et ajoutez un logo d'agence.",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "Ajouter le logo",
          confirmButtonClass: "btn btn-primary btn-lg",
          cancelButtonText: "Pas maintenant",
        }).then(res => {
          if (res.value) {
            this.$router.push({
              name: "profile",
            });
          }
        });
      }
    },
    getBuildingOwnersWithVotingCategoriesForMandates() {
      for (const mandate of this.allMandates) {
        const buildingOwner = BuildingOwnerORM.query()
          .where("id", mandate.building_owner_id)
          .with("building_owner_building_lots.building_owner_voting_rights")
          .first();
        this.buildingOwners.push(buildingOwner);
      }
    },
    getPercentagePerVotingCategory() {
      const totalPresentMandates = this.presentMandates.concat(
        this.representedMandates,
        this.dummyVideoMandates,
        this.representedDummyVideoMandates,
        this.mandatesByCorrespondence
      );

      let partsForPresentMandates = [];
      partsForPresentMandates = this.getTotalPartsOfVotingCategoryFromMandates(
        totalPresentMandates
      );

      let result = [];

      for (const partsOfTotalMandatesPerVotingCategory of this.partsForTotalMandates) {
        const partsOfPresentMandatesPerVotingCategory = partsForPresentMandates.find(
          item =>
            item.votingCategoryName == partsOfTotalMandatesPerVotingCategory.votingCategoryName
        );
        if (partsOfPresentMandatesPerVotingCategory) {
          const percent = (
            (partsOfPresentMandatesPerVotingCategory.parts * 100) /
            partsOfTotalMandatesPerVotingCategory.parts
          ).toFixed(2);
          result.push({
            name: partsOfTotalMandatesPerVotingCategory.votingCategoryName,
            percent,
          });
        } else {
          result.push({
            name: partsOfTotalMandatesPerVotingCategory.votingCategoryName,
            percent: 0,
          });
        }
      }
      return result;
    },
    getTotalPartsOfVotingCategoryFromMandates(mandates) {
      let result = [];
      for (const mandate of mandates) {
        const buildingOwner = this.buildingOwners.find(
          item => item.id == mandate.building_owner_id
        );
        for (const boBuilLot of buildingOwner.building_owner_building_lots) {
          for (const boVotingRights of boBuilLot.building_owner_voting_rights) {
            const votingCategory = VotingCategoryORM.find(boVotingRights.voting_category_id);
            const alreadyAddedIndex = result.findIndex(
              item => votingCategory && item.votingCategoryName == votingCategory.name
            );
            if (alreadyAddedIndex != -1) {
              result[alreadyAddedIndex].parts += boVotingRights.parts;
            } else {
              result.push({
                parts: boVotingRights.parts,
                votingCategoryName: votingCategory ? votingCategory.name : "",
              });
            }
          }
        }
      }
      return result;
    },
  },
};
</script>
